/**
 * Shared UI Styles - Enhanced medieval Chinese aesthetic
 * Provides CSS variables and shared styles for all UI components
 */

export const UI_COLORS = {
  // Wood tones
  woodDark: '#2d1f15',
  woodMid: '#4a3728',
  woodLight: '#5c4033',
  woodAccent: '#6b4423',

  // Gold/Metal
  gold: '#d4af37',
  goldLight: '#ffd35a',
  goldDark: '#8b6914',
  bronze: '#b8860b',

  // Text
  textPrimary: '#f5e6d3',
  textSecondary: '#c4a77d',
  textMuted: '#9e8b6e',
  textGold: '#ffd700',

  // Accents
  red: '#c62828',
  blue: '#5b9bd5',
  green: '#4caf50',
};

/**
 * UI texture paths
 * - wood: 木纹边框纹理 - 用于UI面板边框
 * - parchment: 羊皮纸/皮革内衬 - 用于面板内部背景
 * - frame: 金属边框装饰条 - 用于标题栏等装饰
 */
export const UI_TEXTURE_PATHS = {
  wood: '/images/ui/elements/wood.webp',
  parchment: '/images/ui/elements/parchment.webp',
  frame: '/images/ui/elements/frame.webp',
};

/**
 * UI decoration paths - 高品质装饰素材
 * - cornerOrnaments: 龙头角落装饰 (4角精灵图 2x2)
 * - titleBar: 凤凰标题栏装饰带
 * - divider: 金色中国结分割线
 * - buttonStates: 木质按钮状态 (4态精灵图)
 * - minimapFrame: 龙凤小地图边框
 */
export const UI_DECORATION_PATHS = {
  cornerOrnaments: '/images/ui/decorations/corner-ornaments.png',
  titleBar: '/images/ui/decorations/title-bar.png',
  divider: '/images/ui/decorations/divider.png',
  buttonStates: '/images/ui/decorations/button-states.png',
  minimapFrame: '/images/ui/decorations/minimap-frame.png',
};

/**
 * CSS for wood grain texture effect
 */
export const WOOD_GRAIN_CSS = `
  background:
    repeating-linear-gradient(
      92deg,
      transparent 0px,
      rgba(0,0,0,0.02) 1px,
      transparent 2px,
      transparent 5px
    ),
    repeating-linear-gradient(
      0deg,
      transparent 0px,
      rgba(139,90,43,0.08) 3px,
      transparent 6px,
      transparent 12px
    ),
    linear-gradient(145deg, #4a3728 0%, #2d1f15 50%, #3d2a1c 100%);
`;

/**
 * CSS for parchment background texture
 */
export const PARCHMENT_BG_CSS = `
  background:
    linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%),
    url('/images/ui/elements/parchment.webp');
  background-size: cover;
  background-position: center;
`;

/**
 * CSS for panel inner background (parchment-like)
 */
export const PANEL_INNER_CSS = `
  background:
    radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.12) 100%),
    linear-gradient(145deg, rgba(74,55,40,0.95) 0%, rgba(45,31,21,0.98) 100%);
`;

/**
 * CSS for title bar gradient
 */
export const TITLE_BAR_CSS = `
  background: linear-gradient(180deg,
    #6b4423 0%,
    #5a3a1e 25%,
    #4a2f17 50%,
    #5c3a20 75%,
    #6b4423 100%
  );
`;

/**
 * Shared CSS styles to inject into components
 */
export const SHARED_UI_STYLES = `
  /* CSS Variables */
  :root {
    --ui-wood-dark: ${UI_COLORS.woodDark};
    --ui-wood-mid: ${UI_COLORS.woodMid};
    --ui-wood-light: ${UI_COLORS.woodLight};
    --ui-gold: ${UI_COLORS.gold};
    --ui-gold-light: ${UI_COLORS.goldLight};
    --ui-gold-dark: ${UI_COLORS.goldDark};
    --ui-text-primary: ${UI_COLORS.textPrimary};
    --ui-text-secondary: ${UI_COLORS.textSecondary};
    --ui-text-muted: ${UI_COLORS.textMuted};
  }

  /* Enhanced panel base */
  .ui-panel {
    ${WOOD_GRAIN_CSS}
    border: 3px solid var(--ui-gold-dark);
    border-radius: 8px;
    box-shadow:
      inset 0 2px 4px rgba(255,220,150,0.15),
      inset 0 -3px 6px rgba(0,0,0,0.4),
      0 6px 20px rgba(0,0,0,0.6),
      0 0 0 1px rgba(0,0,0,0.3);
    position: relative;
    overflow: hidden;
  }

  /* Panel with extra wood grain detail */
  .ui-panel::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      90deg,
      transparent,
      transparent 4px,
      rgba(0,0,0,0.02) 4px,
      rgba(0,0,0,0.02) 8px
    );
    pointer-events: none;
    z-index: 1;
  }

  /* Metal corner decorations - Simple style */
  .ui-corner {
    position: absolute;
    width: 18px;
    height: 18px;
    z-index: 3;
  }

  .ui-corner::before {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background: linear-gradient(135deg, #e8c252 0%, #d4af37 30%, #b8860b 70%, #8b6914 100%);
    border: 2px solid #d4af37;
    border-radius: 3px;
    box-shadow:
      inset 0 1px 2px rgba(255,255,255,0.4),
      inset 0 -1px 2px rgba(0,0,0,0.3),
      0 2px 4px rgba(0,0,0,0.4);
  }

  /* Corner with rivet detail */
  .ui-corner::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    background: radial-gradient(circle, #ffd35a 0%, #d4af37 50%, #8b6914 100%);
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    box-shadow:
      inset 0 1px 1px rgba(255,255,255,0.6),
      0 1px 2px rgba(0,0,0,0.4);
  }

  .ui-corner.tl { top: -6px; left: -6px; }
  .ui-corner.tr { top: -6px; right: -6px; }
  .ui-corner.bl { bottom: -6px; left: -6px; }
  .ui-corner.br { bottom: -6px; right: -6px; }

  /* Dragon corner ornaments - Using sprite sheet */
  .ui-dragon-corner {
    position: absolute;
    width: 48px;
    height: 48px;
    background-image: url('/images/ui/decorations/corner-ornaments.png');
    background-size: 200% 200%;
    z-index: 3;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
    pointer-events: none;
  }
  .ui-dragon-corner.tl { top: -6px; left: -6px; background-position: 0% 0%; }
  .ui-dragon-corner.tr { top: -6px; right: -6px; background-position: 100% 0%; }
  .ui-dragon-corner.bl { bottom: -6px; left: -6px; background-position: 0% 100%; }
  .ui-dragon-corner.br { bottom: -6px; right: -6px; background-position: 100% 100%; }

  /* Smaller dragon corners for compact panels */
  .ui-dragon-corner.sm {
    width: 32px;
    height: 32px;
  }
  .ui-dragon-corner.sm.tl { top: -4px; left: -4px; }
  .ui-dragon-corner.sm.tr { top: -4px; right: -4px; }
  .ui-dragon-corner.sm.bl { bottom: -4px; left: -4px; }
  .ui-dragon-corner.sm.br { bottom: -4px; right: -4px; }

  /* Enhanced title bar */
  .ui-title-bar {
    ${TITLE_BAR_CSS}
    border-bottom: 2px solid var(--ui-gold-dark);
    padding: 10px 14px;
    position: relative;
    z-index: 2;
  }

  /* Decorative line under title */
  .ui-title-bar::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 20px;
    right: 20px;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(212,175,55,0.5) 20%,
      rgba(212,175,55,0.8) 50%,
      rgba(212,175,55,0.5) 80%,
      transparent 100%
    );
  }

  .ui-title {
    font: 700 16px/1.2 'Noto Serif SC', 'SimSun', serif;
    color: var(--ui-gold-light);
    text-shadow: 0 2px 4px rgba(0,0,0,0.5), 0 0 10px rgba(255,180,0,0.2);
    letter-spacing: 2px;
    text-align: center;
  }

  /* Enhanced button style */
  .ui-btn {
    padding: 8px 12px;
    background: linear-gradient(180deg,
      #5c4033 0%,
      #4a3525 50%,
      #3d2a1c 100%
    );
    border: 2px solid #6b4423;
    border-radius: 4px;
    color: var(--ui-text-secondary);
    cursor: pointer;
    font: 600 13px/1 'Noto Serif SC', 'SimSun', serif;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    transition: all 0.15s ease;
    position: relative;
    overflow: hidden;
  }

  .ui-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%);
    pointer-events: none;
  }

  .ui-btn:hover:not(:disabled) {
    background: linear-gradient(180deg,
      #6b4e3a 0%,
      #5a3d2b 50%,
      #4a3525 100%
    );
    border-color: var(--ui-gold-dark);
    color: var(--ui-gold-light);
    box-shadow: 0 0 8px rgba(212,175,55,0.3);
  }

  .ui-btn:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow: inset 0 2px 4px rgba(0,0,0,0.3);
  }

  .ui-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ui-btn.active {
    background: linear-gradient(180deg,
      #8b6914 0%,
      #6b4423 100%
    );
    border-color: var(--ui-gold);
    color: #fff;
    box-shadow: 0 0 10px rgba(212,175,55,0.5);
  }

  /* Stat row style */
  .ui-stat-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(139,105,20,0.25);
  }

  .ui-stat-row:last-child {
    border-bottom: none;
  }

  .ui-stat-label {
    color: var(--ui-text-secondary);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ui-stat-value {
    font-weight: 700;
    font-size: 15px;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
  }

  .ui-stat-value.gold {
    color: #ffd700;
    text-shadow: 0 0 8px rgba(255,215,0,0.4);
  }

  /* Section divider - Simple line */
  .ui-divider {
    height: 2px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(139,105,20,0.4) 20%,
      rgba(139,105,20,0.6) 50%,
      rgba(139,105,20,0.4) 80%,
      transparent 100%
    );
    margin: 8px 0;
  }

  /* Decorative divider - Using ornament image */
  .ui-divider-ornate {
    height: 24px;
    background: url('/images/ui/decorations/divider.png') center/contain no-repeat;
    margin: 12px 0;
    opacity: 0.85;
  }

  /* Decorative button - Using sprite sheet (4 states) */
  .ui-btn-ornate {
    position: relative;
    padding: 12px 28px;
    background: url('/images/ui/decorations/button-states.png') 0% 50% / 400% auto no-repeat;
    border: none;
    color: #f5e6d3;
    font: 600 14px/1 'Noto Serif SC', serif;
    cursor: pointer;
    transition: all 0.15s;
    text-shadow: 0 1px 2px rgba(0,0,0,0.5);
    min-height: 44px;
  }
  .ui-btn-ornate:hover {
    background-position: 33.33% 50%;
    color: #ffd35a;
  }
  .ui-btn-ornate:active {
    background-position: 66.66% 50%;
  }
  .ui-btn-ornate:disabled {
    background-position: 100% 50%;
    color: #9e8b6e;
    cursor: not-allowed;
  }

  /* Title bar with phoenix decoration */
  .ui-title-bar-ornate {
    position: relative;
  }
  .ui-title-bar-ornate::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 40px;
    background: url('/images/ui/decorations/title-bar.png') center/contain no-repeat;
    z-index: 10;
    pointer-events: none;
    opacity: 0.9;
  }

  /* Card/item style */
  .ui-card {
    background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
    border: 2px solid #6b4423;
    border-radius: 6px;
    padding: 8px;
    transition: all 0.15s ease;
    position: relative;
  }

  .ui-card:hover {
    border-color: var(--ui-gold);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.4);
  }

  .ui-card.selected {
    border-color: var(--ui-gold);
    box-shadow: 0 0 12px rgba(212,175,55,0.4);
  }

  .ui-card.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Icon container */
  .ui-icon-box {
    width: 40px;
    height: 40px;
    background: linear-gradient(180deg, #4a3728 0%, #2d1f15 100%);
    border: 2px solid #5c4033;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .ui-icon-box.active {
    border-color: var(--ui-gold-dark);
    background: linear-gradient(180deg, #5c4033 0%, #3d2a1c 100%);
  }

  /* Cost display with coin icon */
  .ui-cost {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;
    color: #ffd700;
    text-shadow: 0 0 4px rgba(255,215,0,0.3);
  }

  .ui-cost .coin-icon {
    width: 14px;
    height: 14px;
  }

  /* Keyboard hint */
  .ui-kbd {
    background: rgba(0,0,0,0.3);
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px solid rgba(139,105,20,0.5);
    font-family: inherit;
    color: var(--ui-text-secondary);
    font-size: 10px;
    min-width: 20px;
    text-align: center;
  }

  /* Scrollbar styling */
  .ui-scrollable::-webkit-scrollbar {
    width: 8px;
  }

  .ui-scrollable::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.2);
    border-radius: 4px;
  }

  .ui-scrollable::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #6b4423 0%, #4a2f17 100%);
    border-radius: 4px;
    border: 1px solid rgba(139,105,20,0.5);
  }

  .ui-scrollable::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #8b6914 0%, #6b4423 100%);
  }

  /* Parchment texture background */
  .ui-parchment-bg {
    background:
      linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%),
      url('/images/ui/elements/parchment.webp');
    background-size: cover;
    background-position: center;
  }

  /* Frame texture for panel borders */
  .ui-frame-bg {
    background:
      linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%),
      url('/images/ui/elements/frame.webp');
    background-size: cover;
    background-position: center;
  }

  /* Minimap frame with dragon/phoenix ornament */
  .ui-minimap-frame {
    position: relative;
    padding: 24px;
    background: url('/images/ui/decorations/minimap-frame.png') center/100% 100% no-repeat;
    filter: drop-shadow(0 4px 8px rgba(0,0,0,0.5));
  }
  .ui-minimap-frame .minimap-content {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 4px;
  }
`;

/**
 * Inject shared styles into document
 */
export function injectSharedStyles(): void {
  const styleId = 'ui-shared-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = SHARED_UI_STYLES;
  document.head.appendChild(style);
}
